/*
  Blog gallery lightbox.
  Article galleries are paragraphs holding 4+ images (see "Blog image gallery" in theme.css), or a table of photos
  (image row, caption row beneath; used for smaller groups of 2 or 3 with visible captions). In a table the caption
  shown in the lightbox is the visible caption under the photo.
  Clicking (or Enter/Space on) a thumbnail opens that gallery's images full size in the theme's
  PhotoSwipe (assets/photoswipe.js), with next/previous and swipe. Controls match the product gallery; the image's
  alt text (its caption, with the photographer's credit) is shown with the counter directly below the image. The
  lightbox keeps room at the bottom (CAPTION_SPACE) so the caption and the buttons never cover the image.
*/
(() => {
  const MIN_IMAGES = 4;
  const CAPTION_SPACE = 150; // px under the image for caption + prev/close/next bar
  const GAP = 12; // px between image and caption

  const slidesFor = (images, captions) =>
    images.map((img, i) => ({
      src: img.currentSrc || img.src,
      width: img.naturalWidth || 1600,
      height: img.naturalHeight || 1600,
      alt: (captions && captions[i]) || img.alt || '',
    }));

  // the visible caption of a photo in a table: the cell in the same column of the row below the image
  const tableCaption = (img) => {
    const cell = img.closest('td, th');
    const nextRow = cell?.parentElement?.nextElementSibling;
    return nextRow?.children[cell.cellIndex]?.textContent.trim() || '';
  };

  const groups = () => {
    const out = [];
    document.querySelectorAll('.article__content.rte p').forEach((paragraph) => {
      const images = Array.from(paragraph.querySelectorAll(':scope > img'));
      if (images.length >= MIN_IMAGES) out.push({ images, captions: null });
    });
    document.querySelectorAll('.article__content.rte table').forEach((table) => {
      const images = Array.from(table.querySelectorAll('img'));
      if (images.length) out.push({ images, captions: images.map(tableCaption) });
    });
    return out;
  };

  const init = () => {
    const theme = window.FoxTheme;
    if (!theme?.PhotoSwipeLightbox || !theme?.PhotoSwipe) return;

    groups().forEach(({ images, captions }) => {
      images.forEach((img, index) => {
        img.classList.add('blog-gallery__thumb');
        img.setAttribute('role', 'button');
        img.setAttribute('tabindex', '0');
        img.setAttribute('aria-label', `${(captions && captions[index]) || img.alt || 'Image'}: open larger (${index + 1} of ${images.length})`);

        const open = () => {
          // same controls as the product gallery (media-gallery.js): prev · close · next in the bottom bar,
          // styled as circles by photoswipe-component.css; plus a caption (the image's alt text) and counter on top
          const lightbox = new theme.PhotoSwipeLightbox({
            dataSource: slidesFor(images, captions),
            pswpModule: theme.PhotoSwipe,
            bgOpacity: 0.92,
            showHideAnimationType: 'fade',
            arrowPrev: false,
            arrowNext: false,
            zoom: false,
            close: false,
            counter: false,
            preloader: false,
            paddingFn: (viewport) => {
              const side = viewport.x < 750 ? 8 : 24;
              return { top: viewport.x < 750 ? 16 : 32, bottom: CAPTION_SPACE, left: side, right: side };
            },
          });
          lightbox.on('uiRegister', () => {
            const ui = lightbox.pswp?.ui;
            if (!ui) return;
            const arrow = '<svg viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>';
            ui.registerElement({
              name: 'prev', ariaLabel: 'Previous image', order: 1, isButton: true,
              html: arrow.replace('<svg', '<svg class="pswp-icon-prev rtl-flip-x"'),
              onClick: () => lightbox.pswp?.prev(),
            });
            ui.registerElement({
              name: 'close-zoom', ariaLabel: 'Close', order: 2, isButton: true,
              html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
              onClick: () => lightbox.pswp?.close(),
            });
            ui.registerElement({
              name: 'next', ariaLabel: 'Next image', order: 3, isButton: true,
              html: arrow.replace('<svg', '<svg class="pswp-icon-next flip-x"'),
              onClick: () => lightbox.pswp?.next(),
            });
            ui.registerElement({
              name: 'blog-caption', order: 9, isButton: false, appendTo: 'root',
              onInit: (el, pswp) => {
                const update = () => {
                  const alt = pswp.currSlide?.data?.alt || '';
                  const count = `${pswp.currIndex + 1} / ${pswp.getNumItems()}`;
                  el.textContent = alt ? `${alt} · ${count}` : count;
                  place();
                };
                // directly below the image as shown (it moves while opening, zooming and resizing)
                const place = () => {
                  const img = pswp.currSlide?.content?.element;
                  const rect = img?.getBoundingClientRect?.();
                  const max = window.innerHeight - CAPTION_SPACE + GAP;
                  const top = rect && rect.height ? Math.min(rect.bottom + GAP, max) : max;
                  el.style.top = `${Math.round(top)}px`;
                };
                pswp.on('change', update);
                ['resize', 'zoomPanUpdate', 'loadComplete', 'openingAnimationEnd', 'imageSizeChange'].forEach((e) =>
                  pswp.on(e, place));
                update();
              },
            });
          });
          lightbox.on('destroy', () => img.focus({ preventScroll: true }));
          lightbox.init();
          lightbox.loadAndOpen(index);
        };

        img.addEventListener('click', open);
        img.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        });
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
